import React, { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import ChangePasswordForm from '@/components/Profile/ChangePasswordForm';
import { requestChangePassword, requestOTPChangePassword } from '@/api/User';
import { Helmet } from 'react-helmet-async';
import { message } from 'antd';
interface ProfileContextType {
  user: {
    fullname: string;
    email: string;
    phone?: string;
    gender?: string;
    birthday?: string;
    occupation?: string;
    avatar_url?: string;
  };
}

const ChangePasswordTab: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const { user } = useOutletContext<ProfileContextType>();
  const [timer, setTimer] = useState(59);
  const [data, setData] = useState<{ newPassword: string; confirmPassword: string }>({
    newPassword: '',
    confirmPassword: '',
  });

  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (step === 2 && timer > 0) {
      interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [step, timer]);

  const handleSubmit = async (data: { newPassword: string; confirmPassword: string }) => {
    setLoading(true);
    try {
      // Call API to change password
      setData(data);
      await requestOTPChangePassword();
      setStep(2);
    } catch (error) {
      alert('Có lỗi xảy ra!');
    } finally {
      setLoading(false);
    }
  };

  const onOTPConfirm = async (otp: string) => {
    try {
      const a = await requestChangePassword(data.newPassword, data.confirmPassword, otp);
      await new Promise((resolve) => setTimeout(resolve, 3000));
      navigate(0);
    } catch (error) {
      alert('Có lỗi xảy ra!');
    }
  };

  const handleResendOtp = () => {
    setTimer(59);
  };

  return (
    <div className="mx-auto p-4 relative">
      <Helmet>
        <title>Thay đổi mật khẩu | BEQ Reward</title>
      </Helmet>
      {step === 1 && (
        <ChangePasswordForm
          onSubmit={handleSubmit}
          loading={loading}
        />
      )}
      {step === 2 && (
        <>
          <div className="fixed inset-0 bg-black/50 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4 relative">
              <div className="mb-4 relative">
                <button
                  aria-label="Close"
                  className="absolute top-0 right-0 text-gray-500 hover:text-gray-700"
                  onClick={() => setStep(1)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#000000" viewBox="0 0 256 256"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path></svg>
                </button>

                <h3 className="text-xl font-semibold mb-2">Xác nhận OTP</h3>
              </div>

              <div className="flex flex-col justify-center align-middle">
                <div className="mb-4 text-center">
                  <p className="text-gray-700">
                    Vui lòng nhập mã OTP đã được gửi đến email
                    <strong className="mx-1 text-blue-600 !underline">{user?.email}</strong>
                    của bạn để xác nhận việc thay đổi mật khẩu.
                  </p>
                </div>
                <div
                  className="flex justify-center items-center gap-2"
                  onPaste={(e) => {
                    const paste = (e.clipboardData || (window as any).clipboardData).getData('text') || '';
                    const digits = paste.replace(/\D/g, '').slice(0, 6).split('');
                    const inputs = Array.from((e.currentTarget as HTMLElement).querySelectorAll<HTMLInputElement>('input[data-otp]'));
                    digits.forEach((d, i) => {
                      if (inputs[i]) inputs[i].value = d;
                    });
                    if (digits.length > 0) {
                      inputs[Math.min(digits.length - 1, inputs.length - 1)]?.focus();
                    }
                    e.preventDefault();

                    const otpValue = inputs.map(input => input.value).join('');
                    if (otpValue.length === 6) {
                      onOTPConfirm(otpValue);
                    }
                  }}
                >
                  {Array.from({ length: 6 }).map((_, i) => (
                    <input
                      title='OTP Digit'
                      key={i}
                      data-otp
                      id={`otp-${i}`}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      className="w-12 h-12 text-center border rounded-md focus:outline-none"
                      onChange={(e) => {
                        const el = e.target as HTMLInputElement;
                        const v = el.value.replace(/\D/g, '').slice(-1);
                        el.value = v;
                        if (v) {
                          const next = el.nextElementSibling as HTMLInputElement | null;
                          next?.focus();
                        }
                        
                        const inputs = Array.from(el.parentElement?.querySelectorAll('input[data-otp]') || []) as HTMLInputElement[];
                        const currentOtp = inputs.map(i => i.value).join('');
                        if (currentOtp.length === 6) {
                          onOTPConfirm(currentOtp);
                        }
                      }}
                      onKeyDown={(e) => {
                        const el = e.currentTarget as HTMLInputElement;
                        if (e.key === 'Backspace' && !el.value) {
                          const prev = el.previousElementSibling as HTMLInputElement | null;
                          prev?.focus();
                        }
                      }}
                    />
                  ))}
                </div>
                <div className="mt-6 text-center">
                  {timer > 0 ? (
                    <p className="mt-4 text-center text-gray-600">
                      Gửi lại mã OTP trong <span className="font-medium">{timer} giây</span>
                    </p>
                  ) : (
                    <button
                    onClick={handleResendOtp}
                    className="mt-2 text-blue-600 hover:underline font-medium">
                      Gửi lại mã OTP
                    </button>
                  )}

                </div>
              </div>

            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ChangePasswordTab;
